import {
  QueryFunctionContext,
  QueryStatus,
  useQuery,
  UseQueryResult
} from "@tanstack/react-query";
import { isPresent } from "src/utils/checks";
import { Language } from "src/utils/localization/languages/types";
import { customLocationValuesFromSearch } from "src/utils/localization/geolocation/geolocationLoader";
import { stringPresent } from "src/utils/strings";
import { useIsHydrated } from "src/utils/useIsHydrated";
import { MAX_API_RETRIES } from "./retryPolicy";

export class FetchApiError extends Error {
  constructor(public readonly statusCode: number) {
    super("Network response was not ok");
    this.name = "FetchApiError";
  }
}

export type FetchApiStatus =
  | "idle"
  | "pending"
  | "loading"
  | "success"
  | "error";

export interface UseFetchDataResponse<T> {
  isLoading: boolean;
  isError: boolean;
  status: FetchApiStatus;
  statusCode?: number;
  data?: T;
  refetch: () => void;
}

const transformStateBuilder =
  <T, V>(dataTransformer: (data: T) => V) =>
  (state: UseFetchDataResponse<T>): UseFetchDataResponse<V> => ({
    ...state,
    data: state.data ? dataTransformer(state.data) : undefined
  });

export const useFetchApiAndTransform = <T, V>(
  request: RequestInfo | null | undefined,
  dataTransformer: (data: T) => V,
  cacheKey?: (string | undefined)[]
): UseFetchDataResponse<V> => {
  const state = useFetchApi<T>(request, cacheKey);
  const transformState = transformStateBuilder(dataTransformer);

  return transformState(state);
};

// export const useMultipleFetchApi = <T>(
//   requests: RequestInfo[]
// ): UseFetchDataResponse<T[]> => {
//   const userQueries = useQueries({
//     queries: requests.map((request) => ({
//       enabled: isPresent(request),
//       queryKey: [JSON.stringify(request)],
//       queryFn: fetchFunction<T>(request)
//     }))
//   });

//   const isLoading = userQueries.some(({ isLoading }) => isLoading);
//   const isError = userQueries.some(({ isError }) => isError);
//   const data = userQueries.reduce((acc, { data }) => {
//     if (data) acc.push(data as T);
//     return acc;
//   }, [] as T[]);

//   const result: UseFetchDataResponse<T[]> = {
//     isLoading,
//     isError,
//     status: isLoading ? "loading" : isError ? "error" : "success",
//     data: data
//   };

//   return result;
// };

export const fetchFunction =
  <T>(request: RequestInfo | null | undefined) =>
  async (_context: QueryFunctionContext): Promise<T | undefined> => {
    if (!request) return;

    const response = await fetch(request, {
      headers: {
        Accept: "application/json"
      }
    });
    if (!response.ok) {
      throw new FetchApiError(response.status);
    }
    return response.json();
  };

const toUseFetchDataResponse = <T>(
  useQueryResult: UseQueryResult<unknown, unknown>
): UseFetchDataResponse<T> => {
  const { isLoading, isError, data, status, refetch, error } = useQueryResult;
  const statusCode =
    error != null && typeof error === "object" && "statusCode" in error
      ? (error as FetchApiError).statusCode
      : undefined;
  return {
    isLoading: isLoading || status == "pending",
    isError,
    status: toFetchApiStatus(status, isLoading, isError),
    statusCode,
    data: data as T,
    refetch
  };
};

const toFetchApiStatus = (
  queryResultStatus: QueryStatus,
  isLoading: boolean,
  isError: boolean
): FetchApiStatus => {
  if (isLoading) return "loading";

  if (isError) return "error";

  return queryResultStatus;
};

export const useFetchApi = <T>(
  request: RequestInfo | null | undefined,
  cacheKey?: (string | undefined)[],
  enableRetry = true
): UseFetchDataResponse<T> => {
  const validKeys = cacheKey?.filter(isPresent) || [];
  const url = request instanceof Request ? request.url : request;
  const result = useQuery({
    retry: enableRetry ? MAX_API_RETRIES : false,
    queryKey: [url, ...validKeys],
    enabled: isPresent(request),
    queryFn: fetchFunction(request)
  });
  const isHydrated = useIsHydrated();

  // The query cache is shared: a component whose Suspense boundary hydrates
  // late (lazy chunk) may find the data another component already fetched.
  // Rendering it during hydration would not match the server HTML, which
  // never has data, so the hydration render sees the same pending state.
  if (!isHydrated) return toPendingResponse<T>(result);

  return toUseFetchDataResponse<T>(result);
};

const toPendingResponse = <T>(
  useQueryResult: UseQueryResult<unknown, unknown>
): UseFetchDataResponse<T> => ({
  isLoading: true,
  isError: false,
  status: "pending",
  statusCode: undefined,
  data: undefined,
  refetch: useQueryResult.refetch
});

const basePath = process.env.GATSBY_DIGITAL_API_BASE_URL!;

export const buildGetRequest = <T>(
  resourceName: string,
  params?: T
): string => {
  const resource = resourceName[0] == "/" ? resourceName : `/${resourceName}`;
  const search = params
    ? new URLSearchParams(filterUndefinedParams(params)).toString()
    : "";

  return basePath + resource + (search ? `?${search}` : "");
};

const filterUndefinedParams = <T extends Record<string, unknown>>(
  params: T
): T => {
  return Object.keys(params).reduce<T>((acc, key: keyof T) => {
    const value = params[key];
    if (isPresent(value)) acc[key] = value;

    return acc;
  }, {} as T);
};

export const buildGeolocationGetRequest = <QueryParams>(
  resourceName: string,
  params: QueryParams,
  querySearch: string | undefined | null,
  language?: Language
): string => {
  const enhancedParams = stringPresent(querySearch)
    ? customLocationValuesFromSearch(querySearch)
    : {};

  return buildGetRequest(resourceName, {
    ...enhancedParams,
    ...params,
    ...(language ? { language: language.sanityCode } : {})
  });
};
